import { useState } from "react";
import API_URL from "../config";
import PageMeta from "../components/PageMeta";

export default function Apply() {
  const [form, setForm] = useState({
    student_name: "",
    college: "",
    dob: "",
    age: "",
    department: "",
    address: "",
    email: "",
    phone: "",
    internship_from: "",
    internship_to: "",
  });

  const [studentPhoto, setStudentPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [appId, setAppId] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setStudentPhoto(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPhotoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studentPhoto) {
      setError("Please upload your photo");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");
    setAppId("");

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    formData.append("student_photo", studentPhoto);

    try {
      const res = await fetch(`${API_URL}/apply`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json();
      if (result.success) {
        setSuccessMsg("Your application has been submitted successfully!");
        setAppId(result.app_id);
        setForm({
          student_name: "",
          college: "",
          dob: "",
          age: "",
          department: "",
          address: "",
          email: "",
          phone: "",
          internship_from: "",
          internship_to: "",
        });
        setStudentPhoto(null);
        setPhotoPreview(null);
        const fileInput = document.getElementById("student_photo_input");
        if (fileInput) fileInput.value = "";
        setCurrentStep(3);
      } else {
        setError(result.error || "Failed to submit application");
      }
    } catch {
      setError("Server error while submitting application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceedToStep2 = () => {
    return form.student_name && form.college && form.dob && form.department;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-10 px-4">
      <PageMeta
        title="Apply for Internship | SmartGoNext"
        description="Fill out the internship details and submit your application for review and certification."
      />

      <div className="max-w-4xl mx-auto">
        {/* Steps Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                  currentStep === step
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-110"
                    : currentStep > step
                    ? "bg-green-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {currentStep > step ? "✓" : step}
              </div>
              <span
                className={`text-sm hidden sm:block ${
                  currentStep === step
                    ? "text-indigo-600 font-medium"
                    : "text-slate-400"
                }`}
              >
                {step === 1
                  ? "Details"
                  : step === 2
                  ? "Review"
                  : "Done"}
              </span>
              {step < 3 && (
                <div
                  className={`w-12 h-0.5 ${
                    currentStep > step ? "bg-green-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 md:p-10 transition-all duration-500">
          {/* Header */}
          <div className="mb-8 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-medium mb-4">
              📝 Internship Application
            </div>
            <h1 className="text-3xl font-bold text-slate-800">
              Apply for Internship
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Fill out your details accurately. Your certificate will be
              automatically generated upon admin approval once you complete your
              internship.
            </p>
          </div>

          {/* SUCCESS STATE */}
          {currentStep === 3 && successMsg ? (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200 animate-bounce-in">
                <span className="text-4xl">🎉</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Application Submitted!
              </h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                {successMsg} Our team will review your application and contact
                you at your provided email address.
              </p>
              <div className="inline-block bg-white border border-slate-200 rounded-xl px-6 py-3 font-mono text-sm text-slate-700 shadow-sm">
                Application ID:{" "}
                <span className="font-semibold text-indigo-600 text-base">
                  {appId}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-4">
                📌 Please save this Application ID to check your status later.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <button
                  onClick={() => {
                    setCurrentStep(1);
                    setSuccessMsg("");
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-indigo-800 transition shadow-lg shadow-indigo-200"
                >
                  📝 Submit Another Application
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* STEP 1: Personal & Internship Details */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2 mb-4">
                      <span className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                        1
                      </span>
                      Personal Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">👤</span>
                          <input
                            name="student_name"
                            placeholder="Enter full name"
                            value={form.student_name}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          College / Institution <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🏛️</span>
                          <input
                            name="college"
                            placeholder="Enter college name"
                            value={form.college}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          Date of Birth <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={form.dob}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm font-medium text-slate-600">Age</label>
                          <input
                            type="number"
                            name="age"
                            placeholder="Age"
                            value={form.age}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm font-medium text-slate-600">
                            Department <span className="text-red-400">*</span>
                          </label>
                          <input
                            name="department"
                            placeholder="e.g. CSE, IT"
                            value={form.department}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2 mb-4">
                      <span className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                        2
                      </span>
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">📧</span>
                          <input
                            type="email"
                            name="email"
                            placeholder="student@example.com"
                            value={form.email}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          Phone Number <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">📞</span>
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Enter contact number"
                            value={form.phone}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5 mt-5">
                      <label className="text-sm font-medium text-slate-600">
                        Residential Address <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="address"
                        placeholder="Enter full address"
                        rows="3"
                        value={form.address}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition resize-none"
                        required
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2 mb-4">
                      <span className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                        3
                      </span>
                      Internship Period
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          Start Date <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="date"
                          name="internship_from"
                          value={form.internship_from}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                          required
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-slate-600">
                          End Date <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="date"
                          name="internship_to"
                          value={form.internship_to}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2 mb-4">
                      <span className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                        4
                      </span>
                      Photo Upload
                    </h3>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-slate-600">
                        Student Photo <span className="text-red-400">*</span>
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                          {photoPreview ? (
                            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-3xl text-slate-300">📷</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <input
                            id="student_photo_input"
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="w-full border border-slate-300 rounded-xl p-2.5 bg-slate-50 focus:outline-none text-sm"
                            required
                          />
                          <p className="text-xs text-slate-400 mt-1">
                            Upload a clean, professional profile photo (JPG, PNG).
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Review */}
              {currentStep === 2 && (
                <div className="animate-fadeIn">
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
                      <span>👀</span> Review Your Application
                    </h3>
                    <p className="text-sm text-slate-500 mb-6">
                      Please verify your details before submitting.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { label: "Full Name", value: form.student_name, icon: "👤" },
                        { label: "College", value: form.college, icon: "🏛️" },
                        { label: "Date of Birth", value: form.dob, icon: "🎂" },
                        { label: "Age", value: form.age, icon: "📅" },
                        { label: "Department", value: form.department, icon: "📚" },
                        { label: "Email", value: form.email, icon: "📧" },
                        { label: "Phone", value: form.phone, icon: "📞" },
                        { label: "Internship Period", value: `${form.internship_from} → ${form.internship_to}`, icon: "📅" },
                      ].map((item, i) => (
                        <div key={i} className="bg-white rounded-xl p-3 border border-slate-200">
                          <label className="text-xs text-slate-400">{item.label}</label>
                          <p className="text-slate-700 font-medium flex items-center gap-1.5 mt-0.5">
                            <span>{item.icon}</span> {item.value}
                          </p>
                        </div>
                      ))}
                      <div className="md:col-span-2 bg-white rounded-xl p-3 border border-slate-200">
                        <label className="text-xs text-slate-400">Address</label>
                        <p className="text-slate-700 font-medium mt-0.5">{form.address}</p>
                      </div>
                    </div>

                    {photoPreview && (
                      <div className="mt-4 bg-white rounded-xl p-3 border border-slate-200 inline-block">
                        <label className="text-xs text-slate-400 block mb-1">Photo</label>
                        <img src={photoPreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2 mt-6">
                  <span>⚠️</span> {error}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between gap-4 mt-8 pt-6 border-t border-slate-100">
                {currentStep === 2 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="px-6 py-3 border border-slate-300 text-slate-600 rounded-xl font-medium hover:bg-slate-100 transition"
                  >
                    ← Back to Edit
                  </button>
                ) : (
                  <div />
                )}

                {currentStep === 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceedToStep2()}
                    className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-indigo-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-100"
                  >
                    Review Application →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-700 transition disabled:opacity-50 shadow-md shadow-green-100 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "✅ Submit Application"
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Info Box */}
        {currentStep !== 3 && (
          <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-2xl p-5 text-sm text-slate-600">
            <div className="flex items-start gap-3">
              <span className="text-xl">💡</span>
              <div>
                <p className="font-medium text-slate-700 mb-1">What happens after I apply?</p>
                <p>
                  Your application will be reviewed by the admin. Once approved after your internship period, 
                  a certificate will be automatically generated and sent to your email. You can verify your 
                  certificate anytime using the Certificate ID.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}